import * as THREE from 'three';
import type { World } from '../sim/world';
import type { SimEvent } from '../sim/events';
import { Vec3 } from '../core/math';

// Where our shot strikes home: a small mark on the ship hit, held on her
// (in her own frame, so it rides with her) for a moment and fading, with a
// word for what was hit when it matters (a gun dismounted, the rudder, a
// mast). Each ship keeps a tally of our latest hits, which the aiming
// bracket shows.

type What = Extract<SimEvent, { type: 'impact' }>['what'];

interface Mark {
  el: HTMLElement;
  vessel: number;
  local: Vec3;
  age: number;
  life: number;
}

const POOL = 32;
/** Hits on one ship this close together in time are one burst (one broadside). */
const BURST = 4;

const WORDS: Partial<Record<What, string>> = { gun: 'Gun dismounted', rudder: 'Rudder hit', mast: 'Mast hit' };

export class HitMarkers {
  private readonly wrap: HTMLElement;
  private readonly marks: Mark[] = [];
  private readonly spare: HTMLElement[] = [];
  /** Our latest burst of hits on each ship: how many, and when the last struck. */
  private readonly bursts = new Map<number, { n: number; last: number }>();
  /** When we last said a ship was raked. */
  private readonly raked = new Map<number, number>();
  private time = 0;
  private readonly p = new THREE.Vector3();

  constructor(parent: HTMLElement) {
    this.wrap = document.createElement('div');
    this.wrap.className = 'hit-marks';
    parent.appendChild(this.wrap);
  }

  /** Seconds since our shot last struck this ship (Infinity: not lately). */
  since(vessel: number): number {
    const b = this.bursts.get(vessel);
    return b ? this.time - b.last : Infinity;
  }

  /** Our hits on her in the latest burst (0 once it's old). */
  tally(vessel: number): number {
    const b = this.bursts.get(vessel);
    return b && this.time - b.last < BURST ? b.n : 0;
  }

  handle(world: World, events: readonly SimEvent[]): void {
    const me = world.player.id;
    for (const e of events) {
      if (e.type === 'raked' && e.by === me && e.vessel !== me) {
        // One word for a raking broadside, where it went in.
        const v = world.vessels.find((o) => o.id === e.vessel);
        if (!v || this.time - (this.raked.get(v.id) ?? -Infinity) < 2.5) continue;
        this.raked.set(v.id, this.time);
        this.add(v.id, v.ship.worldToLocal(new Vec3(e.x, e.y, e.z), new Vec3()), 'gun', e.end === 'stern' ? 'Raked her stern!' : 'Raked her bow!');
        continue;
      }
      if (e.type !== 'impact' || e.by !== me || e.vessel === me) continue;
      const v = world.vessels.find((o) => o.id === e.vessel);
      if (!v) continue;
      const b = this.bursts.get(v.id);
      if (b && this.time - b.last < BURST) (b.n++, (b.last = this.time));
      else this.bursts.set(v.id, { n: 1, last: this.time });
      const local = v.ship.worldToLocal(new Vec3(e.x, e.y, e.z), new Vec3());
      // Grape and a ball's run through her: hits close together are one mark.
      const near = this.marks.find((m) => m.vessel === v.id && m.age < 0.4 && m.local.distanceTo(local) < 1.6);
      const word = WORDS[e.what];
      if (near && !word) {
        near.age = Math.min(near.age, 0.1);
        continue;
      }
      this.add(v.id, local, e.what, word);
    }
  }

  private add(vessel: number, local: Vec3, what: What, word?: string): void {
    let el = this.spare.pop();
    if (!el) {
      if (this.marks.length >= POOL) {
        const old = this.marks.shift()!;
        el = old.el;
      } else {
        el = document.createElement('div');
        this.wrap.appendChild(el);
      }
    }
    const kind = what === 'sail' || what === 'rigging' ? 'aloft' : what === 'hull' || what === 'rail' || what === 'deck' ? 'hull' : 'hard';
    el.className = `hit-mark ${kind}${word ? ' worded' : ''}`;
    el.innerHTML = word ? `<i></i><span>${word}</span>` : '<i></i>';
    el.style.display = '';
    this.marks.push({ el, vessel, local, age: 0, life: word ? 2.2 : 1.4 });
  }

  update(world: World, camera: THREE.PerspectiveCamera, dt: number): void {
    this.time += dt;
    const w = window.innerWidth, h = window.innerHeight;
    const at = tmp;
    for (let i = this.marks.length - 1; i >= 0; i--) {
      const m = this.marks[i]!;
      m.age += dt;
      const v = world.vessels.find((o) => o.id === m.vessel);
      if (m.age >= m.life || !v || v.ship.sunk) {
        m.el.style.display = 'none';
        this.spare.push(m.el);
        this.marks.splice(i, 1);
        continue;
      }
      v.ship.localToWorld(m.local, at);
      const p = this.p.set(at.x, at.y, at.z).project(camera);
      if (p.z > 1 || Math.abs(p.x) > 1.1 || Math.abs(p.y) > 1.1) {
        m.el.style.opacity = '0';
        continue;
      }
      const u = m.age / m.life;
      // In hard, out slow; a word drifts up as it goes.
      const a = Math.min(1, m.age / 0.06) * (1 - u * u);
      const x = (p.x * 0.5 + 0.5) * w, y = (-p.y * 0.5 + 0.5) * h;
      m.el.style.opacity = a.toFixed(3);
      m.el.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) scale(${(1 + 0.35 * Math.min(1, m.age / 0.25)).toFixed(3)})`;
      m.el.style.setProperty('--rise', `${(-u * 14).toFixed(1)}px`);
    }
  }
}

const tmp = new Vec3();
